import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useAdjustPoints } from '~/hooks/apis/loyalty/useAdjustPoints';

const LARGE_POINTS_THRESHOLD = 100000;

interface Props {
  open: boolean;
  onClose: () => void;
  customerId: string;
  onSuccess: () => void;
}

type Action = 'credit' | 'debit';

export default function LuxLoyaltyAdjustPointsModal({ open, onClose, customerId, onSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [action, setAction] = useState<Action>('credit');
  const [points, setPoints] = useState('');
  const [statusCredits, setStatusCredits] = useState('');
  const [nights, setNights] = useState('');
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const adjustPointsMutation = useAdjustPoints({
    onSuccess: () => {
      enqueueSnackbar(`Points ${action === 'credit' ? 'added' : 'deducted'} successfully`, { variant: 'success' });
      setPoints('');
      setStatusCredits('');
      setNights('');
      setReason('');
      onSuccess();
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to adjust points: ${error.message}`, { variant: 'error' });
    },
  });

  const hasAtLeastOneValue = !!(
    (points && Number(points) > 0) ||
    (statusCredits && Number(statusCredits) > 0) ||
    (nights && Number(nights) > 0)
  );

  const canSubmit = hasAtLeastOneValue && reason.trim() && !adjustPointsMutation.isPending;

  const submitAdjustment = () => {
    adjustPointsMutation.mutate({
      customerId,
      action,
      ...(points && Number(points) > 0 && { basePoints: Number(points) }),
      ...(statusCredits && Number(statusCredits) > 0 && { statusCredits: Number(statusCredits) }),
      ...(nights && Number(nights) > 0 && { numberOfNights: Number(nights) }),
      source: {
        name: 'manual-adjustment',
        reason: reason.trim(),
      },
    });
  };

  const handleSubmit = () => {
    if (Number(points) > LARGE_POINTS_THRESHOLD) {
      setConfirmOpen(true);
    } else {
      submitAdjustment();
    }
  };

  const handleClose = () => {
    setPoints('');
    setStatusCredits('');
    setNights('');
    setReason('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Adjust Points
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manually credit or debit points, status credits, and nights on this customer's Société account.
        </Typography>

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Action *
            </Typography>
            <ToggleButtonGroup
              value={action}
              exclusive
              onChange={(_, val) => val && setAction(val)}
              size="small"
              fullWidth
            >
              <ToggleButton value="credit" color="success">
                Credit
              </ToggleButton>
              <ToggleButton value="debit" color="error">
                Debit
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Points"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              type="number"
              inputProps={{ min: 0 }}
              fullWidth
              placeholder="0"
            />
            <TextField
              label="Status Credits"
              value={statusCredits}
              onChange={(e) => setStatusCredits(e.target.value)}
              type="number"
              inputProps={{ min: 0, max: 1500 }}
              fullWidth
              placeholder="0"
            />
            <TextField
              label="Nights"
              value={nights}
              onChange={(e) => setNights(e.target.value)}
              type="number"
              inputProps={{ min: 0, max: 30 }}
              fullWidth
              placeholder="0"
            />
          </Stack>

          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            placeholder="Provide a reason for this adjustment..."
          />

          <Divider />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="text" onClick={handleClose} disabled={adjustPointsMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={action === 'credit' ? 'success' : 'error'}
              onClick={handleSubmit}
              disabled={!canSubmit}
              endIcon={
                adjustPointsMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />
              }
              sx={{ px: 4 }}
            >
              {adjustPointsMutation.isPending ? 'Submitting...' : action === 'credit' ? 'Credit' : 'Debit'}
            </Button>
          </Box>
        </Stack>
      </DialogContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to {action} <strong>{Number(points).toLocaleString()} points</strong> on this account. This is
            a large adjustment — please confirm this is correct.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={action === 'credit' ? 'success' : 'error'}
            onClick={() => {
              setConfirmOpen(false);
              submitAdjustment();
            }}
          >
            Yes, {action === 'credit' ? 'Credit' : 'Debit'} Points
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
